/**
 * The part of `useOperation()` that imports nothing: the verdict on one answer, and the
 * cache of press names that verdict decides the fate of.
 *
 * What a screen does next about ONE answer to an idempotent press is a function of the
 * answer alone — no store, no router, no fetch.
 *
 * # Why it is a module of its own
 *
 * Every screen in this portal used to decide this for itself, and the decision has two
 * halves that must agree: whether to keep the press's name (`operation_id`) and whether
 * to re-read what the screen is looking at. Eleven screens wrote both halves out by
 * hand, and the chessboard's copy was wrong in a way nothing shouted about — it dropped
 * the name on success only, so a stay whose change the service had closed for good
 * (StayChangeSuperseded, 134) was dragged again under a name that was already shut. The
 * refusal was literally true every time, and the only way out was a page reload.
 *
 * So the rule lives here once, and `libs/operation.ts` is the only thing that calls it:
 * screens ask for an outcome and never look at a code.
 *
 * # One file, five portals — MIRRORED, do not edit a copy
 *
 * Since v1.567 every portal that sends an idempotent press carries this file: the
 * reception desk, the chain's console, the marketing desk, the floor and the staff's own
 * phone. They are byte for byte the same file, and the copy in `pms-web` is the one that
 * is edited — the others are written from it by `HTTP_API/ErrorAttributes_test.go`
 * (`TestOperationCoreIsTheSameFileInEveryPortal`), which rewrites a copy that has drifted
 * and then fails, exactly as it does for the generated `enums/errorAttributes.ts`.
 *
 * It is copied rather than imported because the portals are five projects and none of
 * them may import another (CLAUDE.md, deviation 29), and a Go test is what holds the
 * copies identical because it is the only place that can see all five at once.
 *
 * What may differ per portal is `libs/operation.ts`, which is thin on purpose: it hands
 * this module the portal's own minting and its own generated attribute map.
 *
 * # It imports NOTHING, deliberately
 *
 * The attribute lookup is handed in rather than imported, which is what lets
 * `tools/operation.test.mjs` read this file straight with `node --test` (deviation 40 —
 * no test runner is approved, so the runner is the one the build's own Node ships).
 * `libs/operation.ts` passes the generated map; a test passes a table of its own.
 *
 * # The order the answer is read in
 *
 *   1. the status, for the cases that are not refusals at all;
 *   2. the refusal's OWN verdict — `retryable` / `operation_closed`, which every
 *      service since v1.566 writes beside `code` and `message`;
 *   3. the generated attributes for its code, which is the fallback for a body that
 *      carries neither — an older service behind the edge during a deploy;
 *   4. the transport, for an answer with no body of ours at all: a gateway's HTML, a
 *      severed connection, a proxy's 502.
 */

/** One code's verdict, in the shape `enums/errorAttributes.ts` generates. */
export interface IOperationAttributes {
    /** The same request under the same name may be sent again. */
    retryable: boolean
    /** The operation that name stands for is closed: forget it and re-read. */
    terminal:  boolean
}

/**
 * - `done`    — it worked. The name is spent; the next press is a new act.
 * - `closed`  — the operation is over and did not do what was asked. Forget the name
 *               AND re-read, because the screen is looking at a world that has moved.
 * - `retry`   — keep the name. The same body may be sent again and may succeed.
 * - `refused` — keep the name. A person has to change something first, and the press
 *               that follows the repair is still one act (CLAUDE.md, deviation 32).
 */
export type OperationVerdict = 'done' | 'closed' | 'retry' | 'refused'

/** The statuses that are not refusals. 204 is a write that answers nothing. */
const SETTLED = [200, 201, 204]

/**
 * Accepted, still being worked out.
 *
 * No operation-bearing route answers this today — the assistant's turn does, and that is
 * a poll rather than an idempotent write (`AssistantConversation.vue`) — and it is
 * handled here so that the first one that does cannot be read as a refusal. Keeping the
 * name is the whole point: a press still running must be waited for under the name it
 * started under, never re-sent under a new one.
 */
const STILL_RUNNING = 202

/** A refusal body of ours, in the shape `PrintHTTP.WriteInternalError` writes. */
interface IRefusalBody {
    code?:             number
    retryable?:        boolean
    operation_closed?: boolean
}

/**
 * The refusal object, or null where the body is not one of ours — a bare-string error
 * (`PrintHTTP.WriteErrorMessage`), a gateway's page, an aborted request.
 */
const refusalOf = (body: unknown): IRefusalBody | null => {
    const raw = (body as { error?: unknown } | null | undefined)?.error

    return typeof raw === 'object' && raw !== null ? raw as IRefusalBody : null
}

export const operationVerdict = (
    statusCode: number | null | undefined,
    body: unknown,
    attributesOf: (code: number) => IOperationAttributes,
): OperationVerdict => {
    const status = statusCode ?? 0

    if (SETTLED.includes(status)) return 'done'
    if (status === STILL_RUNNING) return 'retry'

    const refusal = refusalOf(body)

    // The service's own verdict wins over the generated map, always: it knows which
    // sentinel it raised, and the map is a copy of the same table one deploy behind.
    // Both fields are written unconditionally, so seeing EITHER of them means the
    // service was asked and answered — including when it answered "neither".
    if (typeof refusal?.operation_closed === 'boolean' || typeof refusal?.retryable === 'boolean') {
        if (refusal.operation_closed) return 'closed'

        return refusal.retryable ? 'retry' : 'refused'
    }

    const code = typeof refusal?.code === 'number' ? refusal.code : 0

    if (code) {
        const attributes = attributesOf(code)

        if (attributes.terminal) return 'closed'

        return attributes.retryable ? 'retry' : 'refused'
    }

    // Nothing of ours came back. The request may not even have left, so the name is
    // kept and the press may be made again — which is exactly what the name is for.
    if (!status || status === 429 || status >= 500) return 'retry'

    // A 4xx we cannot read. KEEPING the name is the safe side of this one: a name kept
    // that should have been dropped is a refusal somebody sees twice, and a name dropped
    // that should have been kept is an act done twice — a second payment, a second
    // pallet note, a second stocktake.
    return 'refused'
}

/** The names of the presses still waiting for a settled answer, by key. */
export interface IOperationNames {
    /** The name this key is already pressing under, or a new one held from now on. */
    nameFor: (key: string, mint: () => string) => string
    /** Applies one verdict: a settled press loses its name, an unsettled one keeps it. */
    settle: (key: string, verdict: OperationVerdict) => void
    forget: (key: string) => void
    forgetAll: () => void
}

/**
 * The cache itself, as a plain function over a Map so that the one rule it exists for
 * can be checked without a browser: **a name survives everything except a settled
 * answer.**
 *
 * `done` and `closed` are the two settled answers — the act happened, or the operation
 * is over and will never happen — and both mean the next press is a NEW act that must
 * carry a new name. `retry` and `refused` are the two unsettled ones: the press is still
 * the same press, either because the service is momentarily busy or because somebody has
 * to change something first, and re-sending it under a fresh name is how one act becomes
 * two (CLAUDE.md, invariant 5).
 *
 * The mint is handed in for the reason the attribute lookup above is: this file reaches
 * for nothing, so `node --test` can read it.
 */
export const createOperationNames = (): IOperationNames => {
    const names = new Map<string, string>()

    return {
        nameFor: (key: string, mint: () => string): string => {
            const held = names.get(key)

            if (held !== undefined) return held

            const minted = mint()

            names.set(key, minted)

            return minted
        },
        settle: (key: string, verdict: OperationVerdict): void => {
            if (verdict === 'done' || verdict === 'closed') names.delete(key)
        },
        forget:    (key: string): void => { names.delete(key) },
        forgetAll: (): void => { names.clear() },
    }
}
